'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { reservationColumn } from '@/components/reservation/res-col2';
import Input from '@/components/ui/form-fields/input';
import Pagination from '@/components/ui/pagination';
import Text from '@/components/ui/typography/text';
import Table from '@/components/ui/table';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';

export default function ListingPage() {
  const [order, setOrder] = useState<string>('desc');
  const [column, setColumn] = useState<string>('');
  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [current, setCurrent] = useState(1);
  const [total, setTotal] = useState(0);

  // Fetch data from the API
  const fetchBookings = async (
    userid: number,
    page: number = 1,
    search: string = '',
  ) => {
    const burl = `${process.env.NEXT_PUBLIC_API}`;
    const offset = (page - 1) * 10;

    try {
      const response = await fetch(
        `${burl}/getlistallmyrentals.php?userid=${userid}&offset=${offset}&search=${search}`,
      );
      if (!response.ok) {
        throw new Error('Failed to fetch bookings.');
      }
      const result = await response.json();
      setData(result.records || []);
      setFilteredData(result.records || []); // Initially set filtered data to fetched data
      setTotal(result.total); // Assuming API provides a total count
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  // Fetch data on component mount and when `current` or `searchFilter` changes
  useEffect(() => {
    let userid = null;

    try {
      if (typeof window !== 'undefined') {
        userid = sessionStorage.getItem('userId');
        fetchBookings(userid, current, searchFilter);
      }
    } catch (error) {
      console.error('sessionStorage is not available during build:', error);
    }

    // Replace this with the actual user ID logic
  }, [current]);

  // Filter data based on search input
  useEffect(() => {
    if (searchFilter.trim() === '') {
      setFilteredData(data); // Reset to full data when search is cleared
    } else {
      const lowerSearch = searchFilter.toLowerCase();
      const filtered = data.filter((record) =>
        Object.values(record).some((value) =>
          String(value).toLowerCase().includes(lowerSearch),
        ),
      );
      setFilteredData(filtered);
    }
  }, [searchFilter, data]);

  // Handle sorting on column header click
  const onHeaderClick = useCallback(
    (value: string) => ({
      onClick: () => {
        setColumn(value);
        setOrder(order === 'desc' ? 'asc' : 'desc');
        const sortedData = [...filteredData].sort((a, b) => {
          if (order === 'desc') return a[value] > b[value] ? -1 : 1;
          return a[value] > b[value] ? 1 : -1;
        });
        setFilteredData(sortedData);
      },
    }),
    [filteredData, order],
  );

  // Prepare columns for the table
  const columns: any = useMemo(
    () =>
      reservationColumn(
        order,
        column,
        undefined, // Replace with actual onSelectAll if needed
        undefined, // Replace with actual onChange if needed
        undefined, // Replace with actual onMore if needed
        onHeaderClick,
      ),
    [order, column, onHeaderClick],
  );

  return (
    <div className="container-fluid mb-12 lg:mb-16">
      <div className="mb-6 mt-8 grid grid-cols-1 items-center gap-3 sm:grid-cols-[1fr_262px] md:mt-10 md:gap-5 lg:mt-12 xl:mt-16 xl:gap-10">
        <Text tag="h4" className="text-xl">
          Your Listings
        </Text>
        <Input
          type="text"
          variant="outline"
          placeholder="Search all fields"
          startIcon={<MagnifyingGlassIcon className="h-auto w-5" />}
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          inputClassName="pl-12"
        />
      </div>
      <Table
        data={filteredData}
        columns={columns}
        variant="minimal"
        className="text-sm"
      />
      <div className="mt-8 flex justify-end">
        <Pagination
          current={current}
          total={total}
          pageSize={10}
          nextIcon="Next"
          prevIcon="Previous"
          prevIconClassName="!text-gray-dark"
          nextIconClassName="!text-gray-dark"
          onChange={(page) => {
            setCurrent(page);
          }}
        />
      </div>
    </div>
  );
}
